import {
  AvatarFullConfig,
  GenConfigFunc,
  Sex,
  EarSize,
  HairStyleMan,
  HairStyleWoman,
  HatStyle,
  EyeStyle,
  GlassesStyle,
  NoseStyle,
  MouthStyle,
  ShirtStyle,
  EyeBrowStyle
} from "./types"

/**
 * Pick random one from the list
 */
interface PickRandomOpt<T> {
  avoidList?: T[]
  usually?: T[]
}
type PickRandomFromList = <T>(
  data: T[],
  opt?: PickRandomOpt<T | undefined>
) => T
export const pickRandomFromList: PickRandomFromList = (
  data,
  { avoidList = [], usually = [] } = {}
) => {
  // Filter out avoid options
  const avoidSet = new Set(avoidList.filter(item => Boolean(item)))
  let myData = data.filter(item => !avoidSet.has(item))

  // Increase selecting possibility of usually options
  const usuallyData = usually
    .filter(Boolean)
    .reduce((acc, cur) => acc.concat(new Array(15).fill(cur)), [] as any[])
  myData = myData.concat(usuallyData)

  // Pick randon one from the list
  const amount = myData.length
  const randomIdx = Math.floor(Math.random() * amount)
  return myData[randomIdx]
}

/**
 * Gennerate avatar configurations
 */
interface DefaultOptions {
  sex: Sex[]
  faceColor: string[]
  earSize: EarSize[]
  hairColor: string[]
  hairStyleMan: HairStyleMan[]
  hairStyleWoman: HairStyleWoman[]
  hatColor: string[]
  hatStyle: HatStyle[]
  eyeBrowWoman: EyeBrowStyle[]
  eyeStyle: EyeStyle[]
  glassesStyle: GlassesStyle[]
  noseStyle: NoseStyle[]
  mouthStyle: MouthStyle[]
  shirtStyle: ShirtStyle[]
  shirtColor: string[]
  bgColor: string[]
  gradientBgColor: string[]
}
export const defaultOptions: DefaultOptions = {
  sex: ["man", "woman"],
  faceColor: ["#F9C9B6", "#AC6651"],
  earSize: ["small", "big"],
  hairColor: [
    "#000",
    "#fff",
    "#77311D",
    "#FC909F",
    "#D2EFF3",
    "#506AF4",
    "#F48150",
    "#ffff00"
  ],
  hairStyleMan: ["normal", "thick", "mohawk"],
  hairStyleWoman: ["normal", "womanLong", "womanShort"],
  hatColor: [
    "#000",
    "#fff",
    "#77311D",
    "#FC909F",
    "#D2EFF3",
    "#506AF4",
    "#F48150",
    "#ffff00"
  ],
  hatStyle: ["beanie", "turban", "none"],
  eyeBrowWoman: ["up", "upWoman"],
  eyeStyle: ["circle", "oval", "smile"],
  glassesStyle: ["round", "square", "none"],
  noseStyle: ["short", "long", "round"],
  mouthStyle: ["laugh", "smile", "peace"],
  shirtStyle: ["hoody", "short", "polo"],
  shirtColor: ["#9287FF", "#6BD9E9", "#FC909F", "#F4D150", "#77311D"],
  bgColor: [
    "#9287FF",
    "#6BD9E9",
    "#FC909F",
    "#F4D150",
    "#E0DDFF",
    "#D2EFF3",
    "#FFEDEF",
    "#FFEBA4",
    "#506AF4",
    "#F48150",
    "#74D153"
  ],
  gradientBgColor: [
    "linear-gradient(45deg, #178bff 0%, #ff6868 100%)",
    "linear-gradient(45deg, #176fff 0%, #68ffef 100%)",
    "linear-gradient(45deg, #ff1717 0%, #ffd368 100%)",
    "linear-gradient(90deg, #36cd1c 0%, #68deff 100%)",
    "linear-gradient(45deg, #3e1ccd 0%, #ff6871 100%)",
    "linear-gradient(45deg, #1729ff 0%, #ff56f7 100%)",
    "linear-gradient(45deg, #56b5f0 0%, #45ccb5 100%)"
  ]
}
export const genConfig: GenConfigFunc = (userConfig = {}) => {
  const response = {} as Required<AvatarFullConfig>
  response.sex = userConfig.sex || pickRandomFromList(defaultOptions.sex)
  response.faceColor =
    userConfig.faceColor || pickRandomFromList(defaultOptions.faceColor)
  response.earSize =
    userConfig.earSize || pickRandomFromList(defaultOptions.earSize)
  response.eyeStyle =
    userConfig.eyeStyle || pickRandomFromList(defaultOptions.eyeStyle)
  response.noseStyle =
    userConfig.noseStyle || pickRandomFromList(defaultOptions.noseStyle)
  response.mouthStyle =
    userConfig.mouthStyle || pickRandomFromList(defaultOptions.mouthStyle)
  response.shirtStyle =
    userConfig.shirtStyle || pickRandomFromList(defaultOptions.shirtStyle)
  response.glassesStyle =
    userConfig.glassesStyle ||
    pickRandomFromList(defaultOptions.glassesStyle, { usually: ["none"] })

  // Hair
  let hairColorAvoidList: string[] = []
  let hairColorUsually: string[] = []
  if (!userConfig.hairColor) {
    switch (response.sex) {
      case "woman": {
        hairColorAvoidList =
          (response.faceColor === defaultOptions.faceColor[1] && ["#77311D"]) ||
          []
        break
      }
      case "man": {
        hairColorUsually = ["#000"]
      }
    }
  }
  response.hairColor =
    userConfig.hairColor ||
    pickRandomFromList(defaultOptions.hairColor, {
      avoidList: hairColorAvoidList,
      usually: hairColorUsually
    })

  let myHairStyle = userConfig.hairStyle
  if (!myHairStyle) {
    switch (response.sex) {
      case "man": {
        myHairStyle = pickRandomFromList(defaultOptions.hairStyleMan, {
          usually: ["normal", "thick"]
        })
        break
      }
      case "woman": {
        myHairStyle = pickRandomFromList(defaultOptions.hairStyleWoman)
        break
      }
    }
  }
  response.hairStyle = myHairStyle

  // Hat
  response.hatStyle =
    userConfig.hatStyle ||
    pickRandomFromList(defaultOptions.hatStyle, { usually: ["none"] })
  response.hatColor =
    userConfig.hatColor || pickRandomFromList(defaultOptions.hatColor)
  const _hairOrHatColor =
    (response.hatStyle === "none" && response.hairColor) || response.hatColor

  // Eyebrow
  if (userConfig.eyeBrowStyle) {
    response.eyeBrowStyle = userConfig.eyeBrowStyle
  } else {
    response.eyeBrowStyle =
      response.sex === "woman"
        ? pickRandomFromList(defaultOptions.eyeBrowWoman)
        : "up"
  }

  // Shirt color
  response.shirtColor =
    userConfig.shirtColor ||
    pickRandomFromList(defaultOptions.shirtColor, {
      avoidList: [_hairOrHatColor]
    })

  // Background color
  if (userConfig.isGradient) {
    response.bgColor =
      userConfig.bgColor || pickRandomFromList(defaultOptions.gradientBgColor)
  } else {
    response.bgColor =
      userConfig.bgColor ||
      pickRandomFromList(defaultOptions.bgColor, {
        avoidList: [_hairOrHatColor, response.shirtColor]
      })
  }

  return response
}

export const convertСonfigToNumber = (config: AvatarFullConfig) => {
  let data = ""

  switch (config.sex) {
    case "man":
      data = data + "0"
      break
    case "woman":
      data = data + "1"
      break
    default:
      data = data + "2"
  }

  switch (config.faceColor) {
    case "#F9C9B6":
      data = data + "0"
      break
    case "#AC6651":
      data = data + "1"
      break
    default:
      data = data + "2"
  }

  switch (config.earSize) {
    case "small":
      data = data + "0"
      break
    case "big":
      data = data + "1"
      break
    default:
      data = data + "2"
  }

  switch (config.hairColor) {
    case "#000":
      data = data + "0"
      break
    case "#fff":
      data = data + "1"
      break
    case "#77311D":
      data = data + "2"
      break
    case "#FC909F":
      data = data + "3"
      break
    case "#D2EFF3":
      data = data + "4"
      break
    case "#506AF4":
      data = data + "5"
      break
    case "#F48150":
      data = data + "6"
      break
    case "#ffff00":
      data = data + "7"
      break
    default:
      data = data + "8"
  }

  switch (config.hairStyle) {
    case "normal":
      data = data + "0"
      break
    case "thick":
      data = data + "1"
      break
    case "mohawk":
      data = data + "2"
      break
    case "womanLong":
      data = data + "3"
      break
    case "womanShort":
      data = data + "4"
      break
    default:
      data = data + "5"
  }

  switch (config.hatColor) {
    case "#000":
      data = data + "0"
      break
    case "#fff":
      data = data + "1"
      break
    case "#77311D":
      data = data + "2"
      break
    case "#FC909F":
      data = data + "3"
      break
    case "#D2EFF3":
      data = data + "4"
      break
    case "#506AF4":
      data = data + "5"
      break
    case "#F48150":
      data = data + "6"
      break
    case "#ffff00":
      data = data + "7"
      break
    default:
      data = data + "8"
  }

  switch (config.hatStyle) {
    case "beanie":
      data = data + "0"
      break
    case "turban":
      data = data + "1"
      break
    case "none":
      data = data + "2"
      break
    default:
      data = data + "3"
  }

  switch (config.eyeBrowStyle) {
    case "up":
      data = data + "0"
      break
    case "upWoman":
      data = data + "1"
      break
    default:
      data = data + "2"
  }

  switch (config.eyeStyle) {
    case "circle":
      data = data + "0"
      break
    case "oval":
      data = data + "1"
      break
    case "smile":
      data = data + "2"
      break
    default:
      data = data + "3"
  }

  switch (config.glassesStyle) {
    case "round":
      data = data + "0"
      break
    case "square":
      data = data + "1"
      break
    case "none":
      data = data + "2"
      break
    default:
      data = data + "3"
  }

  switch (config.noseStyle) {
    case "short":
      data = data + "0"
      break
    case "long":
      data = data + "1"
      break
    case "round":
      data = data + "2"
      break
    default:
      data = data + "3"
  }

  switch (config.mouthStyle) {
    case "laugh":
      data = data + "0"
      break
    case "smile":
      data = data + "1"
      break
    case "peace":
      data = data + "2"
      break
    default:
      data = data + "3"
  }

  switch (config.shirtStyle) {
    case "hoody":
      data = data + "0"
      break
    case "short":
      data = data + "1"
      break
    case "polo":
      data = data + "2"
      break
    default:
      data = data + "3"
  }

  switch (config.shirtColor) {
    case "#9287FF":
      data = data + "0"
      break
    case "#6BD9E9":
      data = data + "1"
      break
    case "#FC909F":
      data = data + "2"
      break
    case "#F4D150":
      data = data + "3"
      break
    case "#77311D":
      data = data + "4"
      break
    default:
      data = data + "5"
  }

  switch (config.bgColor) {
    case "#9287FF":
      data = data + "00"
      break
    case "#6BD9E9":
      data = data + "01"
      break
    case "#FC909F":
      data = data + "02"
      break
    case "#F4D150":
      data = data + "03"
      break
    case "#E0DDFF":
      data = data + "04"
      break
    case "#D2EFF3":
      data = data + "05"
      break
    case "#FFEDEF":
      data = data + "06"
      break
    case "#FFEBA4":
      data = data + "07"
      break
    case "#506AF4":
      data = data + "08"
      break
    case "#F48150":
      data = data + "09"
      break
    case "#74D153":
      data = data + "10"
      break
    case "linear-gradient(45deg, #178bff 0%, #ff6868 100%)":
      data = data + "11"
      break
    case "linear-gradient(45deg, #176fff 0%, #68ffef 100%)":
      data = data + "12"
      break
    case "linear-gradient(45deg, #ff1717 0%, #ffd368 100%)":
      data = data + "13"
      break
    case "linear-gradient(90deg, #36cd1c 0%, #68deff 100%)":
      data = data + "14"
      break
    case "linear-gradient(45deg, #3e1ccd 0%, #ff6871 100%)":
      data = data + "15"
      break
    case "linear-gradient(45deg, #1729ff 0%, #ff56f7 100%)":
      data = data + "16"
      break
    case "linear-gradient(45deg, #56b5f0 0%, #45ccb5 100%)":
      data = data + "17"
      break
    default:
      data = data + "18"
  }

  return data
}

export const convertСonfig = (data: string) => {
  const array = data.split("")
  const bg = array[14] + array[15]
  const config = {} as AvatarFullConfig

  switch (array[0]) {
    case "0":
      config.sex = "man"
      break
    case "1":
      config.sex = "woman"
      break
  }

  switch (array[1]) {
    case "0":
      config.faceColor = "#F9C9B6"
      break
    case "1":
      config.faceColor = "#AC6651"
      break
  }

  switch (array[2]) {
    case "0":
      config.earSize = "small"
      break
    case "1":
      config.earSize = "big"
      break
  }

  switch (array[3]) {
    case "0":
      config.hairColor = "#000"
      break
    case "1":
      config.hairColor = "#fff"
      break
    case "2":
      config.hairColor = "#77311D"
      break
    case "3":
      config.hairColor = "#FC909F"
      break
    case "4":
      config.hairColor = "#D2EFF3"
      break
    case "5":
      config.hairColor = "#506AF4"
      break
    case "6":
      config.hairColor = "#F48150"
      break
    case "7":
      config.hairColor = "#ffff00"
      break
  }

  switch (array[4]) {
    case "0":
      config.hairStyle = "normal"
      break
    case "1":
      config.hairStyle = "thick"
      break
    case "2":
      config.hairStyle = "mohawk"
      break
    case "3":
      config.hairStyle = "womanLong"
      break
    case "4":
      config.hairStyle = "womanShort"
      break
  }

  switch (array[5]) {
    case "0":
      config.hatColor = "#000"
      break
    case "1":
      config.hatColor = "#fff"
      break
    case "2":
      config.hatColor = "#77311D"
      break
    case "3":
      config.hatColor = "#FC909F"
      break
    case "4":
      config.hatColor = "#D2EFF3"
      break
    case "5":
      config.hatColor = "#506AF4"
      break
    case "6":
      config.hatColor = "#F48150"
      break
    case "7":
      config.hatColor = "#ffff00"
      break
  }

  switch (array[6]) {
    case "0":
      config.hatStyle = "beanie"
      break
    case "1":
      config.hatStyle = "turban"
      break
    case "2":
      config.hatStyle = "none"
      break
  }

  switch (array[7]) {
    case "0":
      config.eyeBrowStyle = "up"
      break
    case "1":
      config.eyeBrowStyle = "upWoman"
      break
  }

  switch (array[8]) {
    case "0":
      config.eyeStyle = "circle"
      break
    case "1":
      config.eyeStyle = "oval"
      break
    case "2":
      config.eyeStyle = "smile"
      break
  }

  switch (array[9]) {
    case "0":
      config.glassesStyle = "round"
      break
    case "1":
      config.glassesStyle = "square"
      break
    case "2":
      config.glassesStyle = "none"
      break
  }

  switch (array[10]) {
    case "0":
      config.noseStyle = "short"
      break
    case "1":
      config.noseStyle = "long"
      break
    case "2":
      config.noseStyle = "round"
      break
  }

  switch (array[11]) {
    case "0":
      config.mouthStyle = "laugh"
      break
    case "1":
      config.mouthStyle = "smile"
      break
    case "2":
      config.mouthStyle = "peace"
      break
  }

  switch (array[12]) {
    case "0":
      config.shirtStyle = "hoody"
      break
    case "1":
      config.shirtStyle = "short"
      break
    case "2":
      config.shirtStyle = "polo"
      break
  }

  switch (array[13]) {
    case "0":
      config.shirtColor = "#9287FF"
      break
    case "1":
      config.shirtColor = "#6BD9E9"
      break
    case "2":
      config.shirtColor = "#FC909F"
      break
    case "3":
      config.shirtColor = "#F4D150"
      break
    case "4":
      config.shirtColor = "#77311D"
      break
  }

  switch (bg) {
    case "00":
      config.bgColor = "#9287FF"
      break
    case "01":
      config.bgColor = "#6BD9E9"
      break
    case "02":
      config.bgColor = "#FC909F"
      break
    case "03":
      config.bgColor = "#F4D150"
      break
    case "04":
      config.bgColor = "#E0DDFF"
      break
    case "05":
      config.bgColor = "#D2EFF3"
      break
    case "06":
      config.bgColor = "#FFEDEF"
      break
    case "07":
      config.bgColor = "#FFEBA4"
      break
    case "08":
      config.bgColor = "#506AF4"
      break
    case "09":
      config.bgColor = "#F48150"
      break
    case "10":
      config.bgColor = "#74D153"
      break
    case "11":
      config.bgColor = "linear-gradient(45deg, #178bff 0%, #ff6868 100%)"
      break
    case "12":
      config.bgColor = "linear-gradient(45deg, #176fff 0%, #68ffef 100%)"
      break
    case "13":
      config.bgColor = "linear-gradient(45deg, #ff1717 0%, #ffd368 100%)"
      break
    case "14":
      config.bgColor = "linear-gradient(90deg, #36cd1c 0%, #68deff 100%)"
      break
    case "15":
      config.bgColor = "linear-gradient(45deg, #3e1ccd 0%, #ff6871 100%)"
      break
    case "16":
      config.bgColor = "linear-gradient(45deg, #1729ff 0%, #ff56f7 100%)"
      break
    case "17":
      config.bgColor = "linear-gradient(45deg, #56b5f0 0%, #45ccb5 100%)"
      break
  }

  return config
}
